import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation, useRouteMatch, Route, Switch } from "react-router-dom";
import { useFlowConfig } from "flows/hooks";
import Header from "flows/components/Header";
import PMBreakdown from "./components/PMBreakdown";
import { FLOW_STATE, useSessionCtx, parsePlaidDetailsFromFlowState } from "utils";
import { IframeOrFullPageWrapper } from "components/Container";
import { useIntl } from "react-intl";
import { AddPaymentMethodContent, BankAccountForm } from "components/AddPaymentMethod";
import { useGetProviderPayouts } from "flows/PayoutV2/hooks";
import { FraudCheck } from "components/FraudCheck";

/**
 * PaymentMethodManagement
 * @description
 * Payment Selection Flow
 * @param {object} props
 * @param {OnReady} props.onReady
 * @param {OnComplete} props.onComplete
 */
export default function PaymentMethodManagement({ onReady, onComplete }) {
  useEffect(onReady, [onReady]);
  const history = useHistory();
  const { path } = useRouteMatch();
  const location = useLocation();
  const {
    sessionCtx: { flowState },
    setFlowState,
  } = useSessionCtx();
  const { hideExitButtons } = useFlowConfig();
  const [chooserConfig, setChooserConfig] = useState({
    include: ["paypal"],
    exclude: [],
    addCC: true,
    addPaypal: true,
    addACH: false,
  });

  // Fetch provider payouts description
  const { providerPayoutDesc } = useGetProviderPayouts();

  const { formatMessage } = useIntl();

  const handleCloseButtonClicked = () => {
    onComplete({ state: FLOW_STATE.COMPLETE });
  };

  // Function to handle go back navigation event
  const handleGoBack = useCallback(async () => {
    history.goBack();
  }, [history]);

  // Function to navigate to add payment method screen
  const goAddPMScreen = useCallback(({ chooserConfig, newlyAddedBankAccountId }) => {
    setChooserConfig(chooserConfig);
    const qStr = newlyAddedBankAccountId ? `?newlyAddedBankAccountId=${newlyAddedBankAccountId}` : "";
    history.push(`${path}/add-payment-method${qStr}`);
  }, [history, path]);

  const goToBreakdownScreen = useCallback(() => {
    history.push(`${path}/`);
  }, [history, path]);

  /**
   * useEffect to see if we returned from
   * adding a bank account through Plaid.
   * If we did, let's take the user directly to
   * the add payment method screen where we'll
   * load the user's bank accounts so they can create
   * a Masspay payment method option.
   * Only run this on page load.
  */
  useEffect(() => {
    const { bankAccountId } = parsePlaidDetailsFromFlowState(flowState);
    if (bankAccountId) {
      setFlowState(); // clear flowState after grabbing bankAccountId
      goAddPMScreen({
        chooserConfig: {
          include: ["paypal"],
          exclude: [],
          addCC: false,
          addPaypal: true,
          addACH: true,
        },
        newlyAddedBankAccountId: bankAccountId
      });
    }
  }, [flowState, goAddPMScreen, setFlowState]);

  return (
    <IframeOrFullPageWrapper includeTosAndPrivacy withoutPadding>
      <Header
        title={formatMessage({
          defaultMessage: "Payment Method Management",
          description: "Header title",
        })}
        onBackButtonClick={
          // Conditionally handle go back navigation
          history.location.pathname === path || history.location.pathname.includes("success")
            ? null
            : handleGoBack
        }
        showLanguage={true}
        onCloseButtonClick={!hideExitButtons ? handleCloseButtonClicked : undefined}
      />
      <Switch>
        <Route exact path={`${path}/`}>
          <PMBreakdown
            onAddPaymentMethodClick={goAddPMScreen}
            hideExitButtons={hideExitButtons}
            onCloseButtonClicked={handleCloseButtonClicked}
          />
        </Route>
        <Route exact path={`${path}/add-payment-method`}>
          <>
            <FraudCheck isAsync />
            <AddPaymentMethodContent
              isOpen={true}
              onCancel={goToBreakdownScreen}
              chooserConfig={chooserConfig}
              providerDescriptions={providerPayoutDesc}
              path={path}
              onComplete={goToBreakdownScreen}
              description={formatMessage({
                defaultMessage:
                  "Add a payment method from the options below.",
                description: "Need description here.....",
              })}
            />
          </>
        </Route>
        <Route
          exact
          path={`${path}/masspay-form`}
          component={() => {
            const countryISO = location.state?.countryISO;
            return <BankAccountForm onSuccess={(bank_account_id) => {
              goAddPMScreen({
                chooserConfig: {
                  include: ["paypal"],
                  exclude: [],
                  addCC: false,
                  addPaypal: true,
                  addACH: true,
                },
                newlyAddedBankAccountId: bank_account_id
              });
            }} countryISO={countryISO} />;
          }}
        />
      </Switch>
    </IframeOrFullPageWrapper>
  );
}
